// Pixel-art helpers for the arcade TV: a 5x7 bitmap font, small sprite icons
// and the frog scene sprites. Everything is drawn as crisp SVG rectangles, so
// it stays sharp at any size and needs no web font.

import { useId } from "react";
import type { CSSProperties } from "react";

/* -------------------------------------------------------------------- font */

const G = (s: string) => s.split("/");

const GLYPHS: Record<string, string[]> = {
  A: G(".###./#...#/#...#/#####/#...#/#...#/#...#"),
  B: G("####./#...#/#...#/####./#...#/#...#/####."),
  C: G(".####/#..../#..../#..../#..../#..../.####"),
  D: G("####./#...#/#...#/#...#/#...#/#...#/####."),
  E: G("#####/#..../#..../####./#..../#..../#####"),
  F: G("#####/#..../#..../####./#..../#..../#...."),
  G: G(".####/#..../#..../#..##/#...#/#...#/.####"),
  H: G("#...#/#...#/#...#/#####/#...#/#...#/#...#"),
  I: G("#####/..#../..#../..#../..#../..#../#####"),
  J: G("....#/....#/....#/....#/#...#/#...#/.###."),
  K: G("#...#/#..#./#.#../##.../#.#../#..#./#...#"),
  L: G("#..../#..../#..../#..../#..../#..../#####"),
  M: G("#...#/##.##/#.#.#/#.#.#/#...#/#...#/#...#"),
  N: G("#...#/##..#/#.#.#/#..##/#...#/#...#/#...#"),
  O: G(".###./#...#/#...#/#...#/#...#/#...#/.###."),
  P: G("####./#...#/#...#/####./#..../#..../#...."),
  Q: G(".###./#...#/#...#/#...#/#.#.#/#..##/.####"),
  R: G("####./#...#/#...#/####./#.#../#..#./#...#"),
  S: G(".####/#..../#..../.###./....#/....#/####."),
  T: G("#####/..#../..#../..#../..#../..#../..#.."),
  U: G("#...#/#...#/#...#/#...#/#...#/#...#/.###."),
  V: G("#...#/#...#/#...#/#...#/#...#/.#.#./..#.."),
  W: G("#...#/#...#/#...#/#.#.#/#.#.#/##.##/#...#"),
  X: G("#...#/#...#/.#.#./..#../.#.#./#...#/#...#"),
  Y: G("#...#/#...#/.#.#./..#../..#../..#../..#.."),
  Z: G("#####/....#/...#./..#../.#.../#..../#####"),
  "0": G(".###./#...#/#..##/#.#.#/##..#/#...#/.###."),
  "1": G("..#../.##../..#../..#../..#../..#../.###."),
  "2": G(".###./#...#/....#/...#./..#../.#.../#####"),
  "3": G("####./....#/....#/.###./....#/....#/####."),
  "4": G("...#./..##./.#.#./#..#./#####/...#./...#."),
  "5": G("#####/#..../####./....#/....#/#...#/.###."),
  "6": G(".###./#..../#..../####./#...#/#...#/.###."),
  "7": G("#####/....#/...#./..#../..#../..#../..#.."),
  "8": G(".###./#...#/#...#/.###./#...#/#...#/.###."),
  "9": G(".###./#...#/#...#/.####/....#/....#/.###."),
  "/": G("....#/....#/...#./..#../.#.../#..../#...."),
  "!": G("..#../..#../..#../..#../..#../...../..#.."),
  "-": G("...../...../...../#####/...../...../....."),
  ".": G("...../...../...../...../...../...../..#.."),
  "?": G(".###./#...#/....#/...#./..#../...../..#.."),
};
GLYPHS["\u2014"] = GLYPHS["-"];

interface Run {
  x: number;
  y: number;
  w: number;
  c: string;
}

function runsOf(rows: string[], pal: Record<string, string>, ox = 0, oy = 0): Run[] {
  const out: Run[] = [];
  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const ch = row[x];
      const col = pal[ch];
      if (!col) {
        x++;
        continue;
      }
      let e = x + 1;
      while (e < row.length && row[e] === ch) e++;
      out.push({ x: ox + x, y: oy + y, w: e - x, c: col });
      x = e;
    }
  });
  return out;
}

/** Cap height of the bitmap font in font units. */
const FH = 7;

function layout(text: string): { runs: Run[]; w: number } {
  const runs: Run[] = [];
  let cx = 0;
  for (const ch of text.toUpperCase()) {
    if (ch === " ") {
      cx += 4;
      continue;
    }
    const g = GLYPHS[ch];
    if (!g) {
      cx += 4;
      continue;
    }
    runs.push(...runsOf(g, { "#": "#" }, cx, 0));
    cx += 6;
  }
  return { runs, w: Math.max(1, cx - 1) };
}

export function PixText({
  text,
  h,
  color,
  shadow,
  shadowOff = 0.9,
  gradient,
  className,
  style,
}: {
  text: string;
  /** Cap height in cqw. */
  h: number;
  color: string;
  shadow?: string;
  shadowOff?: number;
  gradient?: [string, string];
  className?: string;
  style?: CSSProperties;
}) {
  const id = useId().replace(/:/g, "");
  const { runs, w } = layout(text);
  const pad = 0.3;
  const off = shadow ? shadowOff : 0;
  const vbW = w + 2 * pad + off;
  const vbH = FH + 2 * pad + off;
  const fill = gradient ? `url(#g${id})` : color;
  const unit = h / FH;
  return (
    <svg
      className={className}
      role="img"
      aria-label={text}
      viewBox={`${-pad} ${-pad} ${vbW} ${vbH}`}
      shapeRendering="crispEdges"
      style={{ width: `${vbW * unit}cqw`, height: `${vbH * unit}cqw`, display: "block", flex: "none", ...style }}
    >
      {gradient && (
        <defs>
          <linearGradient id={`g${id}`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={FH}>
            <stop offset="0" stopColor={gradient[0]} />
            <stop offset="1" stopColor={gradient[1]} />
          </linearGradient>
        </defs>
      )}
      {shadow && (
        <g transform={`translate(${off} ${off})`} fill={shadow} stroke={shadow} strokeWidth={0.5}>
          {runs.map((r, i) => (
            <rect key={i} x={r.x} y={r.y} width={r.w} height={1} />
          ))}
        </g>
      )}
      <g fill={fill} stroke={fill} strokeWidth={0.5}>
        {runs.map((r, i) => (
          <rect key={i} x={r.x} y={r.y} width={r.w} height={1} />
        ))}
      </g>
    </svg>
  );
}

/* ----------------------------------------------------------------- sprites */

export interface Sprite {
  w: number;
  h: number;
  runs: Run[];
}

export function makeSprite(rows: string[], pal: Record<string, string>): Sprite {
  const w = Math.max(...rows.map((r) => r.length));
  return { w, h: rows.length, runs: runsOf(rows.map((r) => r.padEnd(w, ".")), pal) };
}

/** Sprite rectangles, to be placed inside an svg (position it with a wrapper). */
export function SpriteRects({ sprite }: { sprite: Sprite }) {
  return (
    <>
      {sprite.runs.map((r, i) => (
        <rect key={i} x={r.x} y={r.y} width={r.w} height={1} fill={r.c} />
      ))}
    </>
  );
}

/** A sprite as a stand-alone icon, `h` cqw tall. */
export function PixIcon({ sprite, h, className, style }: { sprite: Sprite; h: number; className?: string; style?: CSSProperties }) {
  return (
    <svg
      className={className}
      viewBox={`0 0 ${sprite.w} ${sprite.h}`}
      shapeRendering="crispEdges"
      aria-hidden="true"
      style={{ width: `${(h * sprite.w) / sprite.h}cqw`, height: `${h}cqw`, display: "block", flex: "none", ...style }}
    >
      <SpriteRects sprite={sprite} />
    </svg>
  );
}

const CY = "#35d0ff";
const PK = "#ff4fb0";

export const INVADER_CYAN = makeSprite(
  [
    "..#.....#..",
    "...#...#...",
    "..#######..",
    ".##.###.##.",
    "###########",
    "#.#######.#",
    "#.#.....#.#",
    "...##.##...",
  ],
  { "#": CY },
);

export const INVADER_PINK = makeSprite(
  [
    "...#####...",
    ".#########.",
    "###########",
    "###..#..###",
    "###########",
    "..##...##..",
    ".##.###.##.",
    "##.......##",
  ],
  { "#": PK },
);

const RD = "#e5383b";
const DK = "#8f1d2b";
export const TURKEY = makeSprite(
  [
    "...........r.r.r..",
    "....rr...r.rrrrrr.",
    "...rrrr.rrrrrrrrrr",
    "...rwkrrrrrrrrrrr.",
    "..yrrrrrrrrrrrrrr.",
    "..yyrrrrrrrrrrrr..",
    "...rrrrrrrrrrrr...",
    "....rrrrrrrrrr....",
    ".....dddddddd.....",
    "......y....y......",
    "......y....y......",
    ".....yy...yy......",
  ],
  { r: RD, d: DK, w: "#ffffff", k: "#201020", y: "#ffb52a" },
);

// Document with a magnifying glass.
export const DOC_LENS = makeSprite(
  [
    ".#########.......",
    ".#.......##......",
    ".#.#####..#......",
    ".#........#......",
    ".#.#####..#......",
    ".#........#..mmm.",
    ".#.#####.mmmm..mm",
    ".#......mm.....mm",
    ".#.####.m......mm",
    ".#......mm....mm.",
    ".#########mmmmm..",
    "...........mm.mm.",
    "............mm.mm",
    ".............mm..",
  ],
  { "#": CY, m: "#9be7ff" },
);

export const FROG_HEAD = makeSprite(
  [
    ".gg..gg.",
    "gwwggwwg",
    "gwpggpwg",
    "gggggggg",
    "gkggggkg",
    "ggkkkkgg",
    ".gggggg.",
  ],
  { g: "#4be06a", w: "#ffffff", p: "#102018", k: "#0c4a22" },
);
export const FROG_HEAD_OFF = makeSprite(
  [
    ".gg..gg.",
    "gwwggwwg",
    "gwpggpwg",
    "gggggggg",
    "gkggggkg",
    "ggkkkkgg",
    ".gggggg.",
  ],
  { g: "#39425f", w: "#5a6384", p: "#232a40", k: "#232a40" },
);

export const CHEVRON_R = makeSprite(["##..", ".##.", "..##", "..##", ".##.", "##.."], { "#": "#ffd21f" });
export const CHEVRON_L = makeSprite(["..##", ".##.", "##..", "##..", ".##.", "..##"], { "#": "#ffd21f" });

/* ------------------------------------------------------------- frog scene */

const FROG_PAL = {
  k: "#0b3a1c",
  g: "#3fc650",
  l: "#7ff08a",
  b: "#c8f7a6",
  w: "#ffffff",
  p: "#101820",
  m: "#0b3a1c",
};

function frogRows(): string[] {
  const rep = (c: string, n: number) => c.repeat(n);
  const eye = (top: boolean, pupil: boolean) =>
    top ? ".kkkk." : pupil ? "kwppwk" : "kwwwwk";
  const eyes = (top: boolean, pupil: boolean) => `.${eye(top, pupil)}....${eye(top, pupil)}.`;
  return [
    eyes(true, false),
    eyes(false, false),
    eyes(false, true),
    eyes(false, true),
    eyes(false, false),
    `.${rep("k", 16)}.`,
    `k${rep("g", 3)}${rep("l", 3)}${rep("g", 10)}k`,
    `k${rep("g", 16)}k`,
    `k${rep("g", 4)}m${rep("g", 6)}m${rep("g", 4)}k`,
    `k${rep("g", 5)}${rep("m", 6)}${rep("g", 5)}k`,
    `k${rep("g", 3)}${rep("b", 10)}${rep("g", 3)}k`,
    `.k${rep("g", 2)}${rep("b", 10)}${rep("g", 2)}k.`,
    `.${rep("k", 16)}.`,
  ];
}
export const FROG = makeSprite(frogRows(), FROG_PAL);

export const GHOST = makeSprite(
  [
    "..wwwww..",
    ".wwwwwww.",
    "wwwwwwwww",
    "wwpwwwpww",
    "wwpwwwpww",
    "wwwwwwwww",
    "wwwwmwwww",
    "wwwwwwwww",
    "wwwwwwwww",
    "ww.www.ww",
    "w...w...w",
  ],
  { w: "#eaf1ff", p: "#1d2640", m: "#1d2640" },
);

/** The pixel volcano picture as a scene: picture units are 8 image pixels. */
export const SCENE_W = 128;
export const SCENE_H = 96;
